import {useEffect, useState} from 'react';
import {isMobile} from 'react-device-detect';
import Page from '../../../core/components/page/Page';
import {StyledPageWrapper} from '../finder-settings/styled';
import {Button, Select, Typography} from '../../../core/components';
import {localization} from '../../localization/Localization';
import {colorTheme} from '../../../core/configs';
import {
  ageOptions,
  civilStatusOptions,
  PutAdvanceSearchFilterBodyV2,
  sexOptions,
  withChildrenOptions,
} from './AdvanceSearchOptions';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  useGetFinderSetting,
  usePutAdvancedFilterSettingsV2,
} from '../../server/react-query';
import {RoutePath} from '../../navigation/config/RouteConfig';
import {useHistory} from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export interface AdvancedFilterSchema {
  ageFilter?: string
  sexFilter?: string
  withChildren?: boolean
  civilStatus?: string
}

const advancedFilterSchema = yup.object().shape({
  ageFilter: yup.string().nullable(),
  sexFilter: yup.string().nullable(),
  withChildrenFilter: yup.boolean(),
  civilStatus: yup.string().nullable()
});

const AdvanceSearchFilterPageV2 = () => {
  const {authData, updateAuthData} = useAuth();
  
  const {data: finderSettingData, isFetching} = useGetFinderSetting();
  const history = useHistory();
  const putAdvancedFilterSettings = usePutAdvancedFilterSettingsV2();

  // BE to FE
  const preAgeFilterMap: Record<string, string> = {
    range2: '34-49',
    range1: '18-33',
    fiftyAndAbove: '50 and above',
    '': 'No preference',
  };

  const preSexFilterMap: Record<string, string> = {
    female: 'Female',
    male: 'Male',
    lgbtqia: 'LGBTQIA+',
    '': 'No Preference',
  };

  // FE to BE
  const ageFilterMap: Record<string, string> = {
    '50 and above': 'fiftyAndAbove',
    '18-33': 'Range1',
    '34-49': 'Range2',
    'No preference': '',
  };

  const sexFilterMap: Record<string, string> = {
    Female: 'female',
    Male: 'male',
    'LGBTQIA+': 'Lgbtqia',
    'No Preference': '',
  };

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = useForm<AdvancedFilterSchema>({
    resolver: yupResolver(advancedFilterSchema)
  });

  useEffect(() => {
    if (finderSettingData) {
      var schema: AdvancedFilterSchema = {
        ageFilter: preAgeFilterMap[finderSettingData.ageFilter ?? ""],
        sexFilter: preSexFilterMap[finderSettingData.sexFilter ? finderSettingData.sexFilter.toLowerCase() : ""],
        withChildren: finderSettingData.withChildrenFilter,
        civilStatus: !!finderSettingData.civilStatus ? capitalize(finderSettingData.civilStatus) : "No Preference",
      }
      
      reset(schema);
    }
  }, [finderSettingData])

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getCivilStatus = (value: string): string => {
    return value === "No Preference" ? "" : value.toLowerCase();
  }

  const onSave = async () => {
    var values = getValues();
    var payload: PutAdvanceSearchFilterBodyV2 = {
      ageFilter: values.ageFilter && Object.keys(ageFilterMap).includes(values.ageFilter)
            ? ageFilterMap[values.ageFilter]
            : '',
        sexFilter: values.sexFilter && Object.keys(sexFilterMap).includes(values.sexFilter)
            ? sexFilterMap[values.sexFilter]
            : '',
        civilStatus: getCivilStatus(values.civilStatus ?? ""),
        withChildrenFilter: values.withChildren ?? false
    }

    await putAdvancedFilterSettings
      .mutateAsync(payload)
      .then(() => {
        let updatedData = authData;
        if (updatedData?.user?.filterSetting) {
          updatedData.user.filterSetting.ageFilter = payload.ageFilter;
          updatedData.user.filterSetting.sexFilter = payload.sexFilter;
          updatedData.user.filterSetting.civilStatus = payload.civilStatus;
          updatedData.user.filterSetting.withChildrenFilter = payload.withChildrenFilter;

          updateAuthData(updatedData);
        }

        history.go(-1);
      });
  }

  const handleBack = () => {
    history.replace(RoutePath.FINDER_SETTINGS);
  };

  const resetToDefault = () => {
    var schema: AdvancedFilterSchema = {
      ageFilter: preAgeFilterMap[""],
      sexFilter: preSexFilterMap[""],
      withChildren: false,
      civilStatus: "No Preference",
    }
      
    reset(schema);
  };

  return (
    <Page
      showLoading={isFetching}
      showBackButton
      showDesktopBackButton
      showDesktopSaveButton={!isMobile}
      showRightText={isMobile}
      rightText="Save"
      onRightActionClick={handleSubmit(onSave)}
      onBackClick={handleBack}
      rightActionIsLoading={putAdvancedFilterSettings.isLoading}>
      <StyledPageWrapper>
        <Typography
          label={localization.hdr_advance_search_filters}
          variant="f3"
          weight="semibold"
          align="center"
          color={colorTheme.dark}
        />

        <div className="mt-4">

          <div className='flex flex-col space-y-4'>
            {/* Age Filter */}
            <div className="pt-1 space-y-2">
              <Typography
                label="Age"
                variant="f1"
                weight="normal"
                color={colorTheme.darkFaded}
              />
              <div className='country'>
                <Controller
                  control={control}
                  name="ageFilter"
                  render={({ field: { value, onChange }, fieldState }) => (
                    <Select
                      placeholder="Select Age Range"
                      items={ageOptions}
                      itemRenderer={(item, selected) => (
                        <Typography
                          label={item}
                          variant="f1"
                          color={colorTheme.dark}
                          weight={selected ? 'semibold' : 'normal'}
                          className="flex-1"
                        />
                      )}
                      onChange={onChange}
                      value={value}
                      valueRenderer={value => value}
                      error={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Sex Filters */}
            <div>
              <Typography
                label="Sex"
                variant="f1"
                weight="normal"
                color={colorTheme.darkFaded}
              />
              <div className="mt-2">
                <Controller
                  control={control}
                  name="sexFilter"
                  render={({ field: { value, onChange }, fieldState }) => (
                    <Select
                      placeholder={'Select Sex'}
                      items={sexOptions}
                      itemRenderer={(item, selected) => (
                        <Typography
                          label={item}
                          variant="f1"
                          color={colorTheme.dark}
                          weight={selected ? 'semibold' : 'normal'}
                          className="flex-1"
                        />
                      )}
                      onChange={onChange}
                      value={value}
                      valueRenderer={value => value}
                      error={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Civil Statusd With Children Filters */}
            <div>
              <Typography
                label="Civil Status"
                variant="f1"
                weight="normal"
                color={colorTheme.darkFaded}
              />
              <div className="mt-2">
                <Controller
                  control={control}
                  name="civilStatus"
                  render={({ field: { value, onChange }, fieldState }) => (
                    <Select
                      placeholder={'Select Status'}
                      items={civilStatusOptions}
                      itemRenderer={(item, selected) => (
                        <Typography
                          label={item}
                          variant="f1"
                          color={colorTheme.dark}
                          weight={selected ? 'semibold' : 'normal'}
                          className="flex-1"
                        />
                      )}
                      onChange={onChange}
                      value={value}
                      valueRenderer={value => value}
                      error={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div>
              <Typography
                label="With Children"
                variant="f1"
                weight="normal"
                color={colorTheme.darkFaded}
              />
              <div className="mt-2">
                <Controller
                  control={control}
                  name="withChildren"
                  render={({ field: { value, onChange }, fieldState }) => (
                    <Select
                      placeholder="Select option"
                      items={withChildrenOptions}
                      itemRenderer={(item, selected) => (
                        <Typography
                          label={item}
                          variant="f1"
                          color={colorTheme.dark}
                          weight={selected ? 'semibold' : 'normal'}
                          className="flex-1"
                        />
                      )}
                      onChange={selectedValue => {
                        onChange(
                          selectedValue === 'Yes'
                            ? true
                            : selectedValue === 'No Preference'
                            ? false
                            : null,
                        );
                      }}
                      value={
                        value === null
                          ? ''
                          : value === true
                          ? 'Yes'
                          : 'No Preference'
                      }
                      valueRenderer={value =>
                        value === '' ? 'Select option' : value
                      }
                      error={fieldState?.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className='flex justify-items-end'>
              <Button
                label={localization.btn_reset}
                color="primary"
                variant="outlined"
                onClick={resetToDefault}
              />
            </div>
          </div>
        </div>
      </StyledPageWrapper>
    </Page>
  );
};
export default AdvanceSearchFilterPageV2;
